import { useState } from 'react';
import { FileEmbed, Markdown } from './Markdown';
import { fileIdOf } from '../lib/files';

/** Collapse when source is longer than this (dense ops timeline). */
const FOLD_CHAR_THRESHOLD = 200;
/** Or when the markdown source spans more than this many lines. */
const FOLD_LINE_THRESHOLD = 4;

function shouldFold(text: string): boolean {
  if (text.length > FOLD_CHAR_THRESHOLD) return true;
  return text.split('\n').length > FOLD_LINE_THRESHOLD;
}

interface Media {
  /** the `![alt](src)` source as written */
  raw: string;
  src: string;
  alt: string;
}

/** `![alt](src)` / `![alt](src "title")`. */
const IMAGE_MD = /!\[([^\]]*)\]\(\s*<?([^)\s>]+)>?(?:\s+"[^"]*")?\s*\)/g;

/**
 * Split a note into its text (media refs removed, blank runs collapsed) and the media it
 * embeds. Fenced code is left alone — an image ref there is text, not media.
 */
export function splitMedia(md: string): { text: string; media: Media[] } {
  const media: Media[] = [];
  const parts = md.split(/(^```[^\n]*\n[\s\S]*?^```[ \t]*$)/m);
  const text = parts
    .map((part, i) =>
      i % 2
        ? part
        : part.replace(IMAGE_MD, (raw: string, alt: string, src: string) => {
            media.push({ raw, src, alt });
            return '';
          }),
    )
    .join('')
    .replace(/[ \t]+$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return { text, media };
}

/**
 * Timeline note/comment body: long TEXT starts collapsed with a Show more / Show less
 * control (same ghost affordance as Edit). Media never hides behind the fold: only the
 * text length decides folding, and while collapsed the text is shown without its media,
 * which follows as a strip of thumbnails. Expanded = the note as written, media in place.
 */
export function FoldableMarkdown({ children }: { children: string }) {
  const { text, media } = splitMedia(children);
  const foldable = shouldFold(text);
  const [expanded, setExpanded] = useState(false);
  const collapsed = foldable && !expanded;

  return (
    <div className="foldable-note">
      <div className={collapsed ? 'foldable-note-body is-collapsed' : 'foldable-note-body'}>
        <Markdown>{collapsed && media.length ? text : children}</Markdown>
      </div>
      {collapsed && media.length > 0 && (
        <div className="foldable-media" data-testid="fold-media-strip">
          {media.map((m, i) => {
            const id = fileIdOf(m.src);
            // external images go through <Markdown> so its url policy still applies
            return id ? <FileEmbed key={`${id}-${i}`} id={id} alt={m.alt} /> : <Markdown key={`${m.src}-${i}`}>{m.raw}</Markdown>;
          })}
        </div>
      )}
      {foldable && (
        <button
          type="button"
          className="btn btn-sm btn-ghost foldable-note-toggle"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}
